/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
/*
	An interface to control all of the supported PIXI.DisplayObject.destroy() options.
 */
export interface IDestroyOptions {
	children?: boolean; // Destroys all children of the source as well
	texture?: boolean; // Destroys the texture of the source object
	baseTexture?: boolean; // Destroys the BaseTexture of the source object. WARNING: This will destroy the texture across ALL instances, even unrelated objects not yet destroyed.
}

/**
 * Helper functions to easily build new IDestroyOption objects
 */
export class DestroyOptions {
	/**
	 * IncludeChildren
	 * Will destroy the source and all children.
	 * This is equivalent to manually invoking thing.destroy() on each object in the source.children array.
	 */
	public static includeChildren: IDestroyOptions = {
		children: true,
	};

	/**
	 * Destroy Children and Texture
	 * Will destroy the associated texture with the object and all children
	 */
	public static includeChildrenAndTexture: IDestroyOptions = {
		children: true,
		texture: true,
	};
	/**
	 * Destroy All
	 * This will destroy the source object, it's associated texture, all children AND the BaseTexture
	 * WARNING: Destroying the BaseTexture will destroy all instances of that texture across the entire application
	 * Think of the BaseTexture as an image that lives in a cache. A Texture object can have different settings to control the BaseTexture
	 * But it still has a hard reference to the BaseTexture. If the BaseTexture is destroyed, every Texture that references it will become blank/empty.
	 * You want to destroy the BaseTexture when shutting down the entire canvas application, and want all texture cache objects removed as well.
	 */
	public static all: IDestroyOptions = {
		children: true,
		texture: true,
		baseTexture: true,
	};
	/**
	 * Destroy Source Only
	 * This will only Destroy the source object and will keep all children alive.
	 * If there are children in the parent (destroyed) object, the children will be hanging objects and will need
	 * explicitly re-attached to the scene graph.
	 */
	public static sourceOnly = {
		children: false,
		texture: false,
		baseTexture: false,
	};
}
